import React from "react";
import PropTypes from "prop-types";
import {
  InputText,
  Label /* , Dropdown */,
} from "../../../../components/Inputs";
import { /* STATUS_LABELS,  */ STATUS_IDS } from "../../../../components/Utils";
import { RadioButton } from "primereact/radiobutton";
import { PermissionResource, Resources } from "../../../Permissions";

const WORKPLACE_LABELS = {
  GERMANY: "Deutschland",
  EUROPE: "Europa",
  OUTSIDE_EUROPE: "außerhalb Europa",
};

function ADConnectionForm({
  forwardedRef,
  className = "",
  model = {
    adConnection: "Ja",
    adUsername: "",
    adPassword: "",
    adCellNumber: "",
    ngumUserId: "",
    workplace: "",
    workplaceData: {
      name: "",
      action_id: 0,
      status_id: STATUS_IDS.NEW,
    },
  },
  disabled,
  ngumUserIdReadable,
  ngumUserIdEditable,
}) {
  const generateState = React.useCallback(
    (props) => {
      const data = model || {};
      const adStatus_id =
        data.adStatus_id && data.adStatus_id !== ""
          ? data.adStatus_id
          : STATUS_IDS.NEW;
      return {
        adConnection: data.adConnection || "Ja",
        ngumUserId: data.ngumUserId || "",
        workplace: data.workplace || "",
        workplaceData: {
          name: "",
          action_id: 0,
          status_id: 0,
        },
        adCellNumber: data.adCellNumber || "",
        adStatus_id,
        error: {
          workplace: null,
        },
      };
    },
    [model],
  );

  const [state, setState] = React.useState(generateState());

  React.useEffect(() => {
    setState(generateState());
  }, [generateState]);

  const handleChange = React.useCallback((key, value) => {
    setState((prev) => {
      const workplaceData = { ...prev.workplaceData };
      if (key === "workplace") {
        workplaceData.name = value;
      }
      return {
        ...prev,
        [key]: value,
        workplaceData,
        error: {
          ...prev.error,
          [key]: value === "",
        },
      };
    });
  }, []);

  const getEmptyModel = React.useCallback(() => {
    return {
      adConnection: "Ja",
      ngumUserId: "",
      workplace: "",
      adCellNumber: "",
    };
  }, []);

  const validate = React.useCallback(
    (initialize) => {
      const { error, ...currentModel } = state;
      if (currentModel.workplace === "") {
        setState((prev) => ({
          ...prev,
          error: { workplace: true },
        }));
        return null;
      }
      if (initialize) {
        setState(getEmptyModel());
      }
      return {
        ...model,
        ...currentModel,
      };
    },
    [state, getEmptyModel, model],
  );

  const getModel = React.useCallback(
    (initialize = false) => validate(initialize),
    [validate],
  );
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
  }));

  const { ngumUserId, workplace, adCellNumber, adStatus_id, error } = state;
  return (
    <div className={className}>
      <div
        className={`form-group-item ${error.workplace ? "error-group" : ""}`}
      >
        <Label htmlFor="workplace">{`Arbeitsort*`}</Label>
        <WorkplaceRadioGroup
          className="pad-sm-top pad-btm"
          workplace={workplace}
          onChange={(value) => handleChange("workplace", value)}
          disabled={disabled}
        />
      </div>
      {(!model ||
        (model.adConnection === "" && adStatus_id === STATUS_IDS.NEW)) && (
        <div className="form-group-item pad-top-">
          <Label htmlFor="adCellNumber">
            {`optional: Handynummer zur Vorregistrierung des Authenticators`}
          </Label>
          <InputText
            id="adCellNumber"
            className="w3-block w3-border"
            value={adCellNumber}
            placeholder="+49XXX"
            onChange={(event) =>
              handleChange("adCellNumber", event.target.value)
            }
          />
        </div>
      )}
      {ngumUserIdReadable && (
        <div className="form-group-item pad-top-">
          <Label htmlFor="ngumUserId">{`nGUM User ID`}</Label>
          <InputText
            id="ngumUserId"
            className="w3-block w3-border"
            value={ngumUserId}
            onChange={(event) => handleChange("ngumUserId", event.target.value)}
            readOnly={!ngumUserIdEditable}
          />
        </div>
      )}
    </div>
  );
}

ADConnectionForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    adConnection: PropTypes.string,
    adUsername: PropTypes.string,
    adPassword: PropTypes.string,
    adCellNumber: PropTypes.string,
    vf_email: PropTypes.string,
    workplace: PropTypes.string,
    workplaceData: PropTypes.shape({
      name: PropTypes.string.isRequired,
      status_id: PropTypes.number.isRequired,
      action_id: PropTypes.number,
    }),
  }),
  readOnly: PropTypes.bool,
  vfEmailReadable: PropTypes.bool,
  vfEmailEditable: PropTypes.bool,
};

export default React.forwardRef((props, ref) => (
  <ADConnectionForm {...props} forwardedRef={ref} />
));

export const WorkplaceRadioGroup = ({
  className,
  workplace,
  onChange,
  disabled,
  readonly,
}) => {
  const radioStyle = { display: "flex", alignItems: "center", gap: "4px" };
  const handleChange = (event) => {
    onChange && onChange(event.value);
  };
  if (readonly) {
    return (
      <div className="">
        <label className="p-checkbox-label">{workplace}</label>
      </div>
    );
  }
  return (
    <div className={`w3-row ${className}`}>
      {Object.entries(WORKPLACE_LABELS).map(([key, label], index) => (
        <div className="w3-col s4" key={key}>
          <div className="p-col-12" style={radioStyle}>
            <RadioButton
              inputId={`ch${index}`}
              name="workplace"
              value={label}
              checked={workplace === label}
              onChange={handleChange}
              disabled={disabled}
              className={disabled ? "p-disabled" : ""}
            />
            <label htmlFor={`ch${index}`} className="p-checkbox-label">
              {label}
            </label>
          </div>
        </div>
      ))}
    </div>
  );
};
